
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Loader2, FileText, Download, Calendar } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { format } from "date-fns";

const IndianCurrentAffairsReportGenerator: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reportDate, setReportDate] = useState<Date | undefined>(new Date());
  const [reportContent, setReportContent] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>("generate");

  const generateReport = async () => {
    if (!reportDate) {
      toast.error("Please select a date for the report");
      return;
    }

    setIsLoading(true);
    setReportContent('');
    
    try {
      const formattedDate = format(reportDate, "MMMM dd, yyyy");
      
      const prompt = `Generate a comprehensive and well-organized report of daily current affairs for ${formattedDate}, strictly from the Indian perspective, covering the following categories in detail:
•	National Affairs (including government schemes, policies, bills, initiatives, appointments, defence, and disasters)
•	International Affairs (with a focus on India's relations, global summits involving India, foreign visits)
•	Economy & Banking (GDP updates, inflation, RBI updates, SEBI, reforms, budget news, banking trends)
•	Polity & Governance (Supreme Court rulings, Parliament news, state legislations, constitutional developments)
•	Science & Technology (ISRO, DRDO, AI/Tech advances from Indian institutions, health and biotech)
•	Sports (Indian players, national/international tournaments, medal tallies, events hosted by India)
•	Environment & Ecology (climate initiatives, reports, forest/wildlife news, Indian environmental programs)
•	Important Days & Themes (only if nationally or exam-relevant)
✅ Make the report useful for UPSC, State PCS, SSC, Banking, and Railways exams.
✅ Provide facts, figures, and context.
✅ Avoid generic international news unless India is directly involved.`;

      toast.info("Generating current affairs report. This may take a minute...");
      
      const { data, error } = await supabase.functions.invoke('ai-summary', {
        body: { text: prompt }
      });
      
      if (error) {
        throw error;
      }
      
      if (data && data.summary) {
        setReportContent(data.summary);
        setActiveTab("view");
        toast.success("Current affairs report generated successfully!");
      } else {
        toast.error("Failed to generate report - empty response");
      }
    } catch (error) {
      console.error("Error generating current affairs report:", error);
      toast.error("Failed to generate report: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsLoading(false);
    }
  };

  const exportToHTML = () => {
    if (!reportContent) return;
    
    const formattedDate = reportDate ? format(reportDate, "MMMM dd, yyyy") : "Current Date";
    
    // Create HTML content with improved styling
    let htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Indian Current Affairs Report - ${formattedDate}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; line-height: 1.6; }
          h1 { color: #1d4ed8; text-align: center; margin-bottom: 30px; }
          h2 { color: #1e40af; margin-top: 30px; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px; }
          ul { margin-top: 15px; }
          li { margin-bottom: 12px; }
          .report-header { text-align: center; margin-bottom: 40px; }
          .report-date { font-size: 1.2em; color: #4b5563; margin-bottom: 20px; text-align: center; }
          .footer { margin-top: 40px; text-align: center; color: #6b7280; font-size: 0.8em; border-top: 1px solid #e5e7eb; padding-top: 20px; }
        </style>
      </head>
      <body>
        <div class="report-header">
          <h1>Indian Current Affairs Report</h1>
          <div class="report-date">${formattedDate}</div>
        </div>
    `;
    
    // Process markdown content to HTML
    const processedContent = reportContent
      .replace(/^## (.*?)$/gm, '<h2>$1</h2>')
      .replace(/^- (.*?)$/gm, '<li>$1</li>')
      .replace(/<li>/g, '<ul><li>')
      .replace(/<\/li>/g, '</li></ul>')
      .replace(/<\/ul><ul>/g, '');
    
    htmlContent += processedContent;
    
    // Add footer
    htmlContent += `
        <div class="footer">
          <p>Generated by Edaptica - ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `;
    
    // Create a download link
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Indian-Current-Affairs-${reportDate ? format(reportDate, "yyyy-MM-dd") : "report"}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Report exported as HTML");
  };

  // Improved formatting function for the report content
  const formatReportContent = (content: string) => {
    if (!content) return null;
    
    // Split the content into sections based on markdown headers
    const sections = content.split(/(?=## )/g).filter(Boolean);
    
    return (
      <div className="space-y-6">
        {sections.map((section, sectionIndex) => {
          if (!section.trim()) return null;
          
          // Extract section title
          const titleMatch = section.match(/^## (.*?)(?:\n|$)/);
          const sectionTitle = titleMatch ? titleMatch[1].trim() : '';
          
          // Get section content without the title
          let sectionContent = titleMatch 
            ? section.replace(/^## (.*?)(?:\n|$)/, '').trim() 
            : section.trim();
          
          // Parse bullet points
          const bulletPoints = sectionContent
            .split('\n')
            .filter(line => line.trim().startsWith('- '))
            .map(line => line.trim().replace(/^- /, ''));
          
          return (
            <div key={sectionIndex} className="border rounded-lg p-4 bg-card/50">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b text-primary">
                {sectionTitle}
              </h2>
              
              {bulletPoints.length > 0 ? (
                <ul className="space-y-2 list-disc pl-6">
                  {bulletPoints.map((point, pointIndex) => (
                    <li key={pointIndex} className="text-base">
                      {point}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground italic">
                  No specific updates available for this section.
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Card className="w-full mx-auto">
      <CardContent className="pt-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="generate">Generate Report</TabsTrigger>
            <TabsTrigger value="view" disabled={!reportContent}>View Report</TabsTrigger>
          </TabsList>
          
          <TabsContent value="generate" className="space-y-4 mt-4">
            <div className="flex flex-col space-y-6">
              <div>
                <Label htmlFor="report-date" className="block mb-2">Select Date for Current Affairs Report</Label>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                  <div className="w-full sm:w-64">
                    <DatePicker date={reportDate} setDate={setReportDate} />
                  </div>
                  
                  <Button 
                    onClick={generateReport}
                    disabled={isLoading || !reportDate}
                    className="w-full sm:w-auto"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        Generate Report
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="border rounded-lg p-6 bg-muted/40">
                <h3 className="text-lg font-medium mb-3">About Indian Current Affairs Reports</h3>
                <p className="text-sm text-muted-foreground">
                  This tool generates comprehensive current affairs reports from an Indian perspective, 
                  covering key categories like National Affairs, International Relations, Economy, 
                  Polity & Governance, Science & Technology, Sports, and Environment.
                </p>
                <div className="mt-4">
                  <h4 className="font-medium text-sm">Reports include:</h4>
                  <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground space-y-1">
                    <li>Government schemes, policies, and initiatives</li>
                    <li>International relations with Indian context</li>
                    <li>Economic and banking updates</li>
                    <li>Supreme Court rulings and governance news</li>
                    <li>ISRO, DRDO, and technology advances</li>
                    <li>Indian sports achievements and events</li>
                    <li>Environmental programs and initiatives</li>
                  </ul>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  Perfect for UPSC, State PCS, SSC, Banking, and Railways exam preparation.
                </p>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="view" className="space-y-4 mt-4">
            {reportContent ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">
                    Indian Current Affairs: {reportDate ? format(reportDate, "MMMM dd, yyyy") : "Current Report"}
                  </h3>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={exportToHTML}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export as HTML
                  </Button>
                </div>
                
                <Card className="border rounded-lg">
                  <ScrollArea className="h-[600px]">
                    <div className="p-6">
                      {formatReportContent(reportContent)}
                    </div>
                  </ScrollArea>
                </Card>
              </div>
            ) : (
              <div className="border rounded-lg p-6 text-center bg-muted/40">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                <h3 className="mt-4 text-lg font-medium">No Report Generated Yet</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Select a date and click "Generate Report" to create a comprehensive Indian current affairs report.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default IndianCurrentAffairsReportGenerator;
